import { AUTO_STYLE, keyframes, style } from '@angular/animations';
import { Directive, ElementRef, Input, OnInit } from '@angular/core';
import { Util } from '@libs/utilities/util';

import { NgTimedAnimationService } from '../../shared/services/timed-animation.service';
import { EnumNgEventName } from '../../shared/event-animation.data';
import { INgAnimationEffectOptions, INgAnimationStates } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation State Definitions
 */
function wobbleTop(skew: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	skew = skew ? skew : 12;

	//1 - make transition steps by number of transition state steps
	const difference: number = Util.Number.roundDouble(skew / 6); 						// divide the skew value by total number of transition states
	const skew1 	: number = Util.Number.roundDouble(skew);
	const skew2 	: number = Util.Number.roundDouble(skew1 - difference);
	const skew3 	: number = Util.Number.roundDouble(skew2 - difference);
	const skew4 	: number = Util.Number.roundDouble(skew3 - difference);
	const skew5 	: number = Util.Number.roundDouble(skew4 - difference);
	const skew6 	: number = Util.Number.roundDouble(skew5 - difference);
	const skew7 	: number = Util.Number.roundDouble(skew6 - difference);

	//2 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				visibility 			: AUTO_STYLE,
				transform 			: 'perspective(1px) translateZ(0)',
				'transform-origin' 	: '0 100%',
				easing 				: 'ease',
				offset 				: 0
			}),
			style({
				transform 			: `skew(${Util.Number.toPositive(skew1)}deg)`,
				easing 				: 'ease',
				offset 				: 0.083
			}),
			style({
				transform 			: `skew(-${Util.Number.toPositive(skew2)}deg)`,
				easing 				: 'ease',
				offset 				: 0.165
			}),
			style({
				transform 			: `skew(${Util.Number.toPositive(skew3)}deg)`,
				easing 				: 'ease',
				offset 				: 0.330
			}),
			style({
				transform 			: `skew(-${Util.Number.toPositive(skew4)}deg)`,
				easing 				: 'ease',
				offset 				: 0.495
			}),
			style({
				transform 			: `skew(${Util.Number.toPositive(skew5)}deg)`,
				easing 				: 'ease',
				offset 				: 0.660
			}),
			style({
				transform 			: `skew(-${Util.Number.toPositive(skew6)}deg)`,
				easing 				: 'ease',
				offset 				: 0.825
			}),
			style({
				transform 			: `skew(${Util.Number.toPositive(skew7)}deg)`,
				easing 				: 'ease',
				offset 				: 1
			})
		])
	};
	return animationStates;
}


/**------------------------------------------------------
 * Wobble Top on Event
 */
@Directive({
    selector: '[ngWobbleTopOnEvent]',
    providers: [NgTimedAnimationService],
    standalone: false
})
export class NgWobbleTopOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomAnimationEffectOptions> = null!;
	@Input() targetElement: HTMLElement 						   = null!;

	constructor(
		private elementRef 				: ElementRef,
		private timedAnimationService 	: NgTimedAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the skew value is relative to the animation functionality
		if (this.effectOptions?.skew && this.effectOptions?.skew < 1) throw new Error(`WobbleTopOnEventDirective => ngOnInit => FATAL ERROR: Provided skew value should be positive number: ${this.effectOptions?.skew}`);

		//1 - setup the animation
		this.timedAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: wobbleTop(this.effectOptions?.skew),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay,
				timeout		: this.effectOptions?.timeout || this.effectOptions?.duration
			}
		});
	}
}


/**------------------------------------------------------
 * wobble Top on Mouse Hover
 */
@Directive({
    selector: '[ngWobbleTopOnMouseHover]',
    providers: [NgTimedAnimationService],
    standalone: false
})
export class NgWobbleTopOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomAnimationEffectOptions> = null!;
	@Input() targetElement: HTMLElement 						   = null!;

	constructor(
		private elementRef				: ElementRef,
		private timedAnimationService 	: NgTimedAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the skew value is relative to the animation functionality
		if (this.effectOptions?.skew && this.effectOptions?.skew < 1) throw new Error(`WobbleTopOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided skew value should be positive number: ${this.effectOptions?.skew}`);

		//1 - setup the animation
		this.timedAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: wobbleTop(this.effectOptions?.skew),
			event			: EnumNgEventName.MouseHover,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay,
				timeout 	: this.effectOptions?.timeout || this.effectOptions?.duration
			}
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomAnimationEffectOptions extends INgAnimationEffectOptions {
	skew : number;
}
